import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionHeader from "@/components/site/SectionHeader";
import BrandStripe from "@/components/site/BrandStripe";
import ShoppingJourney from "@/components/site/ShoppingJourney";
import { ALL_PRODUCTS } from "@/data/products";
import useSEO from "@/hooks/useSEO";

export default function Shop() {
  useSEO({
    title: "The Range — 30 Colourways",
    description: "Explore all 30 Good Eyes reading glasses across eight styles: Insight, Edit, Outlook, Sharp, Scope, Foresee, Zoom and Focus. Made from recycled materials and colour-coded by strength.",
    path: "/shop",
  });
  return (
    <div className="min-h-screen bg-white text-[color:var(--ge-navy)]" data-testid="shop-page">
      <Nav />
      <main>
        {/* Collection hero */}
        <section className="relative pt-40 md:pt-48 pb-14">
          <div className="ge-container">
            <SectionHeader
              kicker="Collection"
              title={<>The complete<br /><span style={{ color: "var(--ge-yellow)" }}>Good Eyes</span> range.</>}
              subtitle={
                <>
                  Eight design-led{" "}
                  <span className="font-semibold" style={{ color: "var(--ge-beige)", WebkitTextStroke: "0.5px rgba(0,31,91,0.35)" }}>styles</span>.
                  Thirty variations. Five strengths. Every pair is $65 NZD,
                  designed in New Zealand and made from 100% recycled materials.
                </>
              }
            />

            <div className="mt-10 flex flex-wrap gap-3">
              <Chip color="var(--ge-beige)" fg="var(--ge-navy)">8 Styles</Chip>
              <Chip color="var(--ge-yellow)" fg="var(--ge-navy)">{ALL_PRODUCTS.length} Colourways</Chip>
              <Chip color="var(--ge-green)" fg="#fff">5 Strengths</Chip>
              <Chip color="var(--ge-navy)" fg="#fff">$65 NZD</Chip>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <BrandStripe height={22} />
          </div>
        </section>

        {/* Grid of individual style × colour products */}
        <section className="relative bg-white pt-16 pb-24 md:pb-32">
          <div className="ge-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {ALL_PRODUCTS.map((p, i) => (
                <ProductCard key={p.key} p={p} i={i} />
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <BrandStripe height={22} />
          </div>
        </section>

        <ShoppingJourney />
      </main>
      <Footer />
    </div>
  );
}

const cardAccents = ["var(--ge-yellow)","var(--ge-red)","var(--ge-green)","var(--ge-navy)","var(--ge-beige)"];

const ProductCard = ({ p, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
    className="group relative bg-white rounded-2xl border border-[color:var(--ge-navy)]/10 p-5 flex flex-col overflow-hidden hover:border-[color:var(--ge-navy)]/40 transition-colors"
    data-testid={`product-card-${p.styleSlug}-${p.colourSlug}`}
  >
    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: cardAccents[i % cardAccents.length] }} />

    <div className="mt-3 flex items-start justify-between text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">
      <span>{p.styleName}</span>
      <span>{p.colourName}</span>
    </div>

    <Link
      to={`/product/${p.styleSlug}?colour=${p.colourSlug}`}
      className="mt-2 h-40 md:h-48 flex items-center justify-center relative overflow-hidden"
      aria-label={`${p.styleName} in ${p.colourName}`}
    >
      <img src={p.img} alt={`${p.styleName} in ${p.colourName}`} className="max-h-full w-auto product-img" />
    </Link>

    <div className="mt-4 flex items-baseline justify-between">
      <Link to={`/product/${p.styleSlug}?colour=${p.colourSlug}`} className="text-xl font-bold tight-cap text-[color:var(--ge-navy)] group-hover:text-[color:var(--ge-red)] transition-colors">
        {p.styleName}
      </Link>
      <span className="text-sm font-semibold text-[color:var(--ge-navy)]/70">$65</span>
    </div>
    <div className="mt-1 text-xs font-light text-[color:var(--ge-navy)]/60 flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: p.swatch, boxShadow: "inset 0 0 0 1px rgba(0,31,91,0.1)" }} />
      {p.colourName}
    </div>

    <Link
      to={`/product/${p.styleSlug}?colour=${p.colourSlug}`}
      className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] group-hover:text-[color:var(--ge-red)] transition-colors self-start"
    >
      View <ArrowUpRight size={12} />
    </Link>
  </motion.article>
);

const Chip = ({ children, color, fg }) => (
  <span
    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.24em] font-semibold"
    style={{ color: fg, background: color }}
  >
    {children}
  </span>
);
